import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { VaAlert } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import localStorage from 'platform/utilities/storage/localStorage';

const hasId = value =>
  (typeof value === 'string' || typeof value === 'number') &&
  String(value).trim() !== '';

const readSnapshot = key => {
  try {
    const snapshot = JSON.parse(localStorage.getItem(key));
    if (
      snapshot &&
      typeof snapshot.dismissed === 'boolean' &&
      snapshot.statuses &&
      typeof snapshot.statuses === 'object' &&
      !Array.isArray(snapshot.statuses) &&
      Object.values(snapshot.statuses).every(
        status => typeof status === 'string',
      )
    ) {
      return snapshot;
    }
  } catch (error) {
    // Ignore unavailable storage or an invalid saved snapshot.
  }
  return null;
};

const saveSnapshot = (key, { statuses, dismissed }) => {
  try {
    localStorage.setItem(key, JSON.stringify({ statuses, dismissed }));
  } catch (error) {
    // Keep the alert usable for this visit if storage is unavailable.
  }
};

const ObjectiveStatusAlert = ({ caseId, planId, objectives }) => {
  const [alertState, setAlertState] = useState(null);
  const previousSnapshot = useRef(null);
  const storageKey =
    hasId(caseId) && hasId(planId)
      ? `vre-objective-statuses-v1:${caseId}:${planId}`
      : null;
  const statuses = Object.fromEntries(
    objectives
      .filter(
        objective =>
          hasId(objective.objectiveId) &&
          typeof objective.status === 'string' &&
          objective.status.trim() !== '',
      )
      .map(objective => [
        String(objective.objectiveId),
        objective.status.trim().toLowerCase(),
      ])
      .sort(([firstId], [secondId]) => firstId.localeCompare(secondId)),
  );
  const serializedStatuses = JSON.stringify(statuses);

  useEffect(() => {
    if (!storageKey || serializedStatuses === '{}') {
      setAlertState(null);
      return;
    }

    const currentStatuses = JSON.parse(serializedStatuses);
    const saved =
      previousSnapshot.current?.storageKey === storageKey
        ? previousSnapshot.current
        : readSnapshot(storageKey);
    const hasChanges = Object.entries(currentStatuses).some(
      ([id, status]) => saved?.statuses[id] !== status,
    );
    const dismissed = saved
      ? !hasChanges && saved.dismissed
      : Object.values(currentStatuses).every(
          status => status === 'in progress',
        );
    const nextSnapshot = {
      storageKey,
      statuses: currentStatuses,
      dismissed,
    };

    previousSnapshot.current = nextSnapshot;
    setAlertState(nextSnapshot);
    saveSnapshot(storageKey, nextSnapshot);
  }, [storageKey, serializedStatuses]);

  const dismissAlert = () => {
    const nextSnapshot = { ...alertState, dismissed: true };
    previousSnapshot.current = nextSnapshot;
    setAlertState(nextSnapshot);
    saveSnapshot(storageKey, nextSnapshot);
  };

  if (
    !alertState ||
    alertState.storageKey !== storageKey ||
    alertState.dismissed ||
    serializedStatuses === '{}'
  ) {
    return null;
  }

  return (
    <VaAlert
      status="info"
      visible
      closeable
      closeBtnAriaLabel="Dismiss objective status update notification"
      onCloseEvent={dismissAlert}
      className="clearfix-text vads-u-margin-y--4"
    >
      <p className="vads-u-margin--0">
        Some of your objectives have updated since you last logged in.
      </p>
    </VaAlert>
  );
};

ObjectiveStatusAlert.propTypes = {
  objectives: PropTypes.arrayOf(PropTypes.object).isRequired,
  caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ObjectiveStatusAlert;
